import { useCallback, useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  LinearProgress,
  Typography,
  Box,
} from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import { useSelector } from 'react-redux';
import { RootStateType } from 'src/shared/services/redux/reducers';
import { fileUrl } from 'src/shared/services/utils/url';
import ModalTemplate from 'src/shared/templates/modalTemplate';
import UserAvatar from 'src/shared/components/atoms/userAvatar';
import { ActionButtons } from 'src/shared/components/atoms/actionButtons';
import { GenerateActionButtonIcons } from 'src/shared/components/atoms/generateActionButtonsIcons';
import ConsoleHelper from 'src/shared/helpers/consoleHelper';
import { Colors } from 'src/shared/components/styles';
import { ContentPageResponse } from 'src/shared/services/api/response/contentPageResponse';
import { ContentPageAPI } from 'src/shared/services/api/contentPageAPI';
import { DocumentAPI } from 'src/shared/services/api/documentAPI';
import {
  GetAdminPermission,
  useCanViewStats,
} from 'src/shared/helpers/permissionHelper';
import { MainPageTags } from 'src/shared/components/atoms/mainPageTags';
import { isParentCompany } from 'src/shared/helpers/companyHelper';
import { StatsRangeBadge } from 'src/shared/components/atoms/statsRangeBadge';
import { useContentStats } from 'src/shared/helpers/hooks/useContentStats';
import { useLogContentView } from 'src/shared/helpers/hooks/useLogContentView';
import { toast } from 'react-toastify';
import {
  createNewsContentParserOptions,
  normalizeDocumentLinksForRender,
} from 'src/shared/helpers/documentLinks';

type ModalType = {
  id: string | number | undefined;
  modalOpen: boolean;
  setModalOpen: (arg: boolean) => void;
};
const NewsModal = (props: ModalType) => {
  const { modalOpen, setModalOpen, id } = props;
  const { t } = useTranslation();
  // Global states
  const { user } = useSelector((state: RootStateType) => state.oidc);
  const { userCompany } = useSelector(
    (state: RootStateType) => state.authState,
  );
  // Local states
  const [imageError, setImageError] = useState(false);
  const [newsLoading, setNewsLoading] = useState<boolean>(false);
  const [newsItem, setNewsItem] = useState<ContentPageResponse>();
  const canViewStats = useCanViewStats(newsItem?.companyId);
  const [statsRangeDays, setStatsRangeDays] = useState<number>(365);
  const normalizedContent = normalizeDocumentLinksForRender(
    newsItem?.content ?? '',
  );

  const parserOptions = createNewsContentParserOptions({
    token: user?.access_token,
    onUnavailable: () => toast.error(t('DocumentLinkUnavailable')),
    onDownload: (documentId) => {
      const documentApi = new DocumentAPI();
      documentApi.LogDownloadV2(documentId, 'web').catch(() => undefined);
    },
  });

  const getNewsData = useCallback(async () => {
    setNewsLoading(true);
    try {
      const newsapi = new ContentPageAPI();
      const res = await newsapi.Get(id ?? 0);
      if (res) {
        setNewsItem(res?.result);
        setNewsLoading(false);
      }
    } catch (error) {
      setNewsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    getNewsData();
  }, [getNewsData]);

  const {
    allViews,
    uniqueViews,
    refetch: refetchContentStats,
  } = useContentStats({
    contentPageId: newsItem?.id,
    rangeDays: statsRangeDays,
    enabled: modalOpen && canViewStats,
  });

  useLogContentView({
    contentPageId: newsItem?.id,
    enabled: modalOpen,
    onLogged: refetchContentStats,
  });

  const actionButtonPermission = GetAdminPermission(newsItem?.companyId);

  return (
    <ModalTemplate
      isOpen={modalOpen}
      onClose={() => setModalOpen(false)}
      title={
        <Box>
          <Box
            sx={{
              width: '100%',
            }}
          >
            <CardHeader
              sx={{ height: 80 }}
              avatar={
                <UserAvatar
                  size={40}
                  initials={
                    isParentCompany(newsItem?.companyId)
                      ? 'I'
                      : userCompany?.name?.charAt(0)
                  }
                  color={
                    isParentCompany(newsItem?.companyId)
                      ? Colors.PETROL
                      : 'primary.main'
                  }
                />
              }
              title={
                <Typography variant='h6'>
                  {isParentCompany(newsItem?.companyId)
                    ? 'Instalco'
                    : userCompany?.name}
                </Typography>
              }
              subheader={
                <Typography variant='subtitle1'>
                  {dayjs(newsItem?.publishDate).format('YYYY-MM-DD')}
                </Typography>
              }
            />
          </Box>
          <Box
            px={2}
            pb={1}
            sx={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <MainPageTags
              tags={newsItem?.tags}
              businessAreaTags={newsItem?.businessAreaTags}
            />
            {canViewStats && (
              <StatsRangeBadge
                label={t('ViewsLabel')}
                rangeDays={statsRangeDays}
                value={allViews}
                uniqueValue={uniqueViews}
                onRangeChange={setStatsRangeDays}
              />
            )}
          </Box>
        </Box>
      }
      content={
        <>
          {newsLoading ? (
            <LinearProgress />
          ) : (
            <Card
              sx={{
                maxWidth: '100%',
                boxShadow: 'none',
                display: 'flex',
                flexDirection: 'column',
                borderEndStartRadius: 0,
              }}
            >
              {!imageError && (
                <CardMedia
                  sx={{
                    display: 'flex',
                    alignSelf: 'center',
                    justifyContent: 'center',
                    maxHeight: '50vh',
                    maxWidth: '100%',
                    px: 1,
                    '&.MuiCardMedia-img': {
                      width: 'auto',
                      objectFit: 'contain',
                    },
                  }}
                  image={fileUrl(
                    newsItem?.featuredImageDocumentId,
                    user?.access_token,
                  )}
                  component='img'
                  onError={() => {
                    setImageError(true);
                  }}
                />
              )}
              <Typography
                variant='subtitle1'
                sx={{ px: 1, textAlign: 'center' }}
              >
                {newsItem?.featuredImageText ?? ''}
              </Typography>
              <CardContent>
                <Typography variant='h4' fontWeight={600}>
                  {newsItem?.title}
                </Typography>
                <Typography variant='body2' component='div'>
                  {parse(normalizedContent, parserOptions)}
                </Typography>
              </CardContent>
              <ActionButtons
                items={GenerateActionButtonIcons(
                  // Create
                  undefined,
                  // Edit
                  actionButtonPermission
                    ? `/NewsPage/${newsItem?.parentContentPageId}/${id}/edit`
                    : undefined,
                  // Pin item
                  actionButtonPermission
                    ? () => ConsoleHelper.log('Pin item', id)
                    : undefined,
                )}
              />
            </Card>
          )}
        </>
      }
    />
  );
};
export default NewsModal;
